import { StandardResponse } from "./functions";

/**
 * Tag type enumerations for the Memories platform.
 * Strictly lowercase per database enumeration conventions.
 */
export type TagType = "general" | "user" | "group" | "memory" | "media" | "system";

/**
 * Valid target entity types that can receive tags.
 */
export type TagTargetType = "memory" | "media" | "contact" | "group" | "user";

/**
 * Persisted Tag document in the `tags` Firestore collection.
 */
export interface ITag {
  /** Unique Firestore document identifier. */
  id: string;
  /** Canonical category of the tag (strictly lowercase). */
  tagType: TagType;
  /** User-defined text label or string represented by the tag. */
  value: string;
  /** Canonical AppUser.userId who created the tag. */
  ownerId: string;
  /** Unix epoch millisecond timestamp when the tag was created. */
  createdAt: number;
  /** Unix epoch millisecond timestamp when the tag was last modified. */
  updatedAt?: number;
}

/**
 * Embedded relationship metadata stored in the `tags` map on an entity (Memory, Media, Contact, Group).
 * Maps directly into `Record<string, ITagAssociationMeta>` keyed by `tagId`.
 */
export interface ITagAssociationMeta {
  /** Canonical ID of the tag (matching the map key). */
  tagId: string;
  /** Unix epoch millisecond timestamp when the tag was associated with this entity. */
  addedAt: number;
  /** Canonical AppUser.userId who added the tag to this entity. */
  addedBy: string;
}

/**
 * Request payload to create a new tag.
 */
export interface ICreateTagRequest {
  /** Category of the tag. Defaults to "general". */
  tagType: TagType;
  /** User-defined string value or label of the tag. */
  value: string;
}

/**
 * Request payload to edit an existing tag.
 */
export interface IEditTagRequest {
  /** ID of the tag to edit. */
  tagId: string;
  /** Updated text value of the tag. */
  value: string;
}

/**
 * Request payload to delete an existing tag.
 */
export interface IDeleteTagRequest {
  /** ID of the tag to delete. */
  tagId: string;
}

/**
 * Request payload to query tags.
 */
export interface IGetTagsRequest {
  /** Filter by tag type. */
  tagType?: TagType;
  /** Filter by search substring. */
  search?: string;
  /** Scope of tags: 'user' (caller's tags) or 'all' (platform wide). Defaults to 'user'. */
  scope?: "user" | "all";
  /** Optional array of specific tag IDs to query. */
  tagIds?: string[];
}

/**
 * Request payload to associate a tag with an entity.
 */
export interface IAddTagToEntityRequest {
  /** Target entity collection type. */
  targetType: TagTargetType;
  /** Unique ID of the target entity document. */
  targetId: string;
  /** ID of the tag to associate. */
  tagId: string;
}

/**
 * Request payload to remove a tag association from an entity.
 */
export interface IRemoveTagFromEntityRequest {
  /** Target entity collection type. */
  targetType: TagTargetType;
  /** Unique ID of the target entity document. */
  targetId: string;
  /** ID of the tag to dissociate. */
  tagId: string;
}

/**
 * View model representation of a tag.
 */
export type TagModel = ITag & {
  /** Count of entities referencing this tag, if hydrated. */
  usageCount?: number;
  /** Display name of the user who owns or created the tag. */
  ownerDisplayName?: string;
  /** Role of the viewing user for this tag (e.g. 'viewer', 'owner'). */
  userRole?: string;
  /** Linked media items referencing this tag. */
  mediaList?: any[];
  /** Linked memory items referencing this tag. */
  memoriesList?: any[];
  /** Linked contact items referencing this tag. */
  contactsList?: any[];
  /** Linked group items referencing this tag. */
  groupsList?: any[];
};

/**
 * Request payload for administrative query of all tags.
 */
export interface IGetAllTagsRequest {
  /** Optional filter by tag type. */
  tagType?: TagType;
  /** Optional search query. */
  search?: string;
}

/**
 * Response returned by administrative query of all tags.
 */
export interface IGetAllTagsResponse extends StandardResponse {
  tags?: TagModel[];
}

/**
 * Response returned after creating a tag.
 */
export interface ICreateTagResponse extends StandardResponse {
  tag?: TagModel;
}

/**
 * Response returned after editing a tag.
 */
export interface IEditTagResponse extends StandardResponse {
  tag?: TagModel;
}

/**
 * Response returned when deleting a tag.
 */
export interface IDeleteTagResponse extends StandardResponse {}

/**
 * Response returned when querying tags.
 */
export interface IGetTagsResponse extends StandardResponse {
  tags?: TagModel[];
}

/**
 * Response returned when associating a tag with an entity.
 */
export interface IAddTagToEntityResponse extends StandardResponse {
  tag?: ITagAssociationMeta;
  association?: ITagAssociationMeta;
}

/**
 * Response returned when removing a tag from an entity.
 */
export interface IRemoveTagFromEntityResponse extends StandardResponse {}

